import React, { useState, useEffect, useCallback } from 'react';
import { reportApi, formsApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Eye, Loader2, Table2, Search, Download,
  CalendarClock, LayoutDashboard, Send, BarChart3, FileText, Users,
  Clock, GripVertical, Filter, ArrowUpDown, Share2, Settings, X, ChevronRight
} from 'lucide-react';
import { BarChartWidget, DonutChartWidget, TrendlineWidget, ScorecardWidget, PivotTableWidget } from '../../components/charts/ChartWidgets';

const DATA_SOURCES = [
  { value: 'patients', label: 'Patient Records' },
  { value: 'form_submissions', label: 'Form Submissions' },
  { value: 'appointments', label: 'Appointments' },
  { value: 'vitals', label: 'Vitals' },
  { value: 'tasks', label: 'Tasks' },
];

const PATIENT_FIELDS = [
  { key: 'first_name', label: 'First Name', type: 'text' },
  { key: 'last_name', label: 'Last Name', type: 'text' },
  { key: 'mrn', label: 'MRN', type: 'text' },
  { key: 'date_of_birth', label: 'DOB', type: 'date' },
  { key: 'gender', label: 'Gender', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'email', label: 'Email', type: 'text' },
  { key: 'insurance_provider', label: 'Insurance', type: 'text' },
  { key: 'address', label: 'Address', type: 'text' },
  { key: 'primary_language', label: 'Language', type: 'text' },
  { key: 'emergency_contact_name', label: 'Emergency Contact', type: 'text' },
  { key: 'medical_practitioner', label: 'Practitioner', type: 'text' },
  { key: 'created_at', label: 'Created Date', type: 'date' },
];

const CHART_TYPES = [
  { value: 'table', label: 'Table' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'donut', label: 'Donut' },
  { value: 'trendline', label: 'Trendline' },
  { value: 'pivot', label: 'Pivot Table' },
  { value: 'scorecard', label: 'Scorecards' },
];

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list, build, view
  const [forms, setForms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);

  // Builder state
  const [reportName, setReportName] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [dataSource, setDataSource] = useState('patients');
  const [joinSource, setJoinSource] = useState('');
  const [joinFormId, setJoinFormId] = useState('');
  const [selectedFormId, setSelectedFormId] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [groupByField, setGroupByField] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [chartType, setChartType] = useState('table');
  const [filters, setFilters] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // View state
  const [viewReport, setViewReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [rptRes, formsRes] = await Promise.all([reportApi.list(), formsApi.list()]);
      setReports(rptRes.data.reports || []);
      setForms(formsRes.data.forms || []);
    } catch (err) { console.error('ReportsPage: loadReports failed', err); }
    finally { setLoading(false); }
  };

  // Load available fields when data source changes
  const loadFields = useCallback(async () => {
    let fields = [];
    if (dataSource === 'patients') {
      fields = [...PATIENT_FIELDS];
    } else if (dataSource === 'form_submissions' && selectedFormId) {
      try {
        const res = await formsApi.get(selectedFormId);
        const ff = (res.data.fields || []).filter(f => !['section','info','signature','reminder'].includes(f.field_type));
        fields = [
          { key: 'submitted_at', label: 'Date', type: 'date' },
          { key: 'patient_name', label: 'Patient', type: 'text' },
          { key: 'submitted_by_name', label: 'Submitted By', type: 'text' },
          ...ff.map(f => ({ key: f.field_id, label: f.label, type: f.field_type === 'number' ? 'number' : 'text' }))
        ];
      } catch (err) { console.error('ReportsPage: load form fields failed', err); }
    } else if (dataSource === 'vitals') {
      fields = [
        { key: 'recorded_at', label: 'Date', type: 'date' },
        { key: 'patient_name', label: 'Patient', type: 'text' },
        { key: 'blood_pressure_systolic', label: 'Systolic BP', type: 'number' },
        { key: 'blood_pressure_diastolic', label: 'Diastolic BP', type: 'number' },
        { key: 'heart_rate', label: 'Heart Rate', type: 'number' },
        { key: 'temperature', label: 'Temperature', type: 'number' },
        { key: 'oxygen_saturation', label: 'SpO2', type: 'number' },
        { key: 'weight', label: 'Weight', type: 'number' },
      ];
    } else if (dataSource === 'appointments') {
      fields = [
        { key: 'scheduled_date', label: 'Date', type: 'date' },
        { key: 'scheduled_time', label: 'Time', type: 'text' },
        { key: 'patient_name', label: 'Patient', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' },
        { key: 'appointment_type', label: 'Type', type: 'text' },
        { key: 'reason', label: 'Reason', type: 'text' },
      ];
    } else if (dataSource === 'tasks') {
      fields = [
        { key: 'title', label: 'Title', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' },
        { key: 'priority', label: 'Priority', type: 'text' },
        { key: 'patient_name', label: 'Patient', type: 'text' },
        { key: 'created_at', label: 'Created', type: 'date' },
        { key: 'due_date', label: 'Due Date', type: 'date' },
      ];
    }
    // Join: add patient fields if joining with patients
    if (joinSource === 'patients' && dataSource !== 'patients') {
      PATIENT_FIELDS.forEach(pf => {
        if (!fields.some(f => f.key === pf.key)) {
          fields.push({ ...pf, label: `Patient: ${pf.label}`, joined: true });
        }
      });
    }
    // Join: add form fields
    if (joinSource === 'form_submissions' && joinFormId && dataSource === 'patients') {
      try {
        const res = await formsApi.get(joinFormId);
        const ff = (res.data.fields || []).filter(f => !['section','info','signature','reminder'].includes(f.field_type));
        ff.forEach(f => fields.push({ key: `form_${f.field_id}`, label: `Form: ${f.label}`, type: f.field_type === 'number' ? 'number' : 'text', joined: true, formFieldId: f.field_id }));
      } catch (err) { console.error('ReportsPage: load join form fields failed', err); }
    }
    setAvailableFields(fields);
  }, [dataSource, selectedFormId, joinSource, joinFormId]);

  useEffect(() => { loadFields(); }, [loadFields]);

  const toggleColumn = (field) => {
    setSelectedColumns(prev => prev.some(c => c.key === field.key) ? prev.filter(c => c.key !== field.key) : [...prev, field]);
  };

  const addFilter = () => setFilters(prev => [...prev, { field: '', operator: 'equals', value: '' }]);
  const removeFilter = (i) => setFilters(prev => prev.filter((_, idx) => idx !== i));

  const openBuilder = (report = null) => {
    if (report) {
      setEditingReportId(report.id);
      setReportName(report.name);
      setReportDesc(report.description || '');
      setChartType(report.chart_type || 'table');
      // Restore builder state from saved config if available
    } else {
      setEditingReportId(null);
      setReportName('');
      setReportDesc('');
      setDataSource('patients');
      setJoinSource('');
      setJoinFormId('');
      setSelectedFormId('');
      setSelectedColumns([]);
      setGroupByField('');
      setSortField('');
      setChartType('table');
      setFilters([]);
      setDateFrom('');
      setDateTo('');
    }
    setMode('build');
  };

  const handleGenerate = async () => {
    if (!reportName.trim()) { toast.error('Report name required'); return; }
    if (selectedColumns.length === 0) { toast.error('Select at least one column'); return; }
    setGenerating(true);
    try {
      const reportType = dataSource === 'form_submissions' ? 'form_report' : dataSource === 'patients' && joinFormId ? 'patient_records' : 'patient_demographics';
      const payload = {
        name: reportName,
        description: reportDesc,
        report_type: reportType,
        form_id: selectedFormId || joinFormId || null,
        selected_columns: selectedColumns.map(c => c.formFieldId || c.key),
        date_range_start: dateFrom || null,
        date_range_end: dateTo || null,
        filters: { columns: selectedColumns.map(c => ({ key: c.key, label: c.label })), group_by: groupByField, sort_field: sortField, sort_dir: sortDir, join_source: joinSource, join_form_id: joinFormId, data_source: dataSource },
      };
      let reportId = editingReportId;
      if (editingReportId) {
        await reportApi.update(editingReportId, { ...payload, chart_type: chartType });
      } else {
        const res = await reportApi.generate(payload);
        reportId = res.data.report_id;
      }
      if (reportId) {
        await reportApi.update(reportId, { chart_type: chartType }).catch(() => {});
      }
      toast.success(editingReportId ? 'Report updated' : 'Report generated');
      setMode('list');
      loadReports();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGenerating(false); }
  };

  const openView = async (report) => {
    try {
      const res = await reportApi.get(report.id);
      setViewReport(res.data);
      setReportData(res.data.data);
      setChartType(res.data.chart_type || 'table');
      if (res.data.form_id) {
        try {
          const cd = await formsApi.chartData(res.data.form_id);
          setChartData(cd.data);
        } catch (err) { console.error('ReportsPage: chart data failed', err); }
      }
      setMode('view');
    } catch (err) { console.error('ReportsPage: load report failed', err); toast.error('Failed to load'); }
  };

  const handleDelete = async (id) => {
    try {
      await reportApi.delete(id);
      toast.success('Report deleted');
      loadReports();
    } catch { toast.error('Failed to delete'); }
  };

  const exportCsv = () => {
    if (!reportData?.rows) return;
    const cols = reportData.columns || [];
    const csv = [cols.map(c => c.label).join(','), ...reportData.rows.map(r => cols.map(c => `"${(r[c.key] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${viewReport?.name || 'report'}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success('Exported');
  };

  const filteredRows = (reportData?.rows || []).filter(row => {
    if (!searchTerm) return true;
    return Object.values(row).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const renderChart = () => {
    if (!reportData?.rows?.length) return null;
    const charts = chartData?.charts || {};
    const numericCharts = Object.values(charts).filter(c => c.numeric);
    const catCharts = Object.values(charts).filter(c => !c.numeric && c.frequency?.length > 0);
    const cols = reportData.columns || [];

    switch (chartType) {
      case 'bar':
        return <div className="grid md:grid-cols-2 gap-4">
          {numericCharts.length > 0 ? numericCharts.map(c => <Card key={c.field_id} className="careiq-card"><CardContent className="p-4"><BarChartWidget data={c.frequency} title={c.label} /></CardContent></Card>) :
            catCharts.length > 0 ? catCharts.map(c => <Card key={c.field_id} className="careiq-card"><CardContent className="p-4"><BarChartWidget data={c.frequency} title={c.label} /></CardContent></Card>) :
            <Card className="careiq-card col-span-2"><CardContent className="p-4"><BarChartWidget data={filteredRows.slice(0, 20).map((r, i) => ({ label: r[cols[1]?.key] || `#${i+1}`, count: 1 }))} title="Distribution" /></CardContent></Card>}
        </div>;
      case 'donut':
        return <div className="grid md:grid-cols-2 gap-4">
          {(catCharts.length > 0 ? catCharts : numericCharts).map(c => <Card key={c.field_id} className="careiq-card"><CardContent className="p-4"><DonutChartWidget data={c.frequency} title={c.label} /></CardContent></Card>)}
        </div>;
      case 'trendline':
        return <div className="grid md:grid-cols-2 gap-4">
          {numericCharts.map(c => <Card key={c.field_id} className="careiq-card"><CardContent className="p-4"><TrendlineWidget data={c.trendline || []} title={c.label} /></CardContent></Card>)}
        </div>;
      case 'pivot':
        return <PivotTableWidget data={filteredRows} title={viewReport?.name} columns={cols} />;
      case 'scorecard':
        return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ScorecardWidget label="Total Records" value={reportData.total} color="#0E7490" />
          {numericCharts.map(c => <ScorecardWidget key={c.field_id} label={`Avg ${c.label}`} value={c.avg} subtitle={`${c.min} — ${c.max}`} color="#7C3AED" />)}
          {reportData.summary && Object.entries(reportData.summary).map(([k, v]) => <ScorecardWidget key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? JSON.stringify(v) : v} color="#14B8A6" />)}
        </div>;
      default: return null;
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;

  // ==================== LIST VIEW ====================
  if (mode === 'list') return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Reports</h1><p className="text-sm text-[#64748B]">Build, visualize, and share reports</p></div>
        <Button className="careiq-btn-primary" onClick={() => openBuilder()} data-testid="create-report-btn"><Plus className="w-4 h-4 mr-2" />New Report</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reports.map(r => (
          <Card key={r.id} className="careiq-card hover:shadow-md transition-shadow" data-testid={`report-card-${r.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1 cursor-pointer" onClick={() => openView(r)}>
                  <h3 className="font-semibold text-sm text-[#0A2342]">{r.name}</h3>
                  <span className="text-[10px] text-[#64748B]">{r.report_type?.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex gap-0.5">
                  <Button size="sm" variant="ghost" onClick={() => openView(r)}><Eye className="w-3.5 h-3.5 text-[#0E7490]" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openBuilder(r)} data-testid={`edit-report-${r.id}`}><Edit className="w-3.5 h-3.5 text-[#64748B]" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} data-testid={`delete-report-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button>
                </div>
              </div>
              {r.description && <p className="text-xs text-[#64748B] line-clamp-2">{r.description}</p>}
              <div className="flex items-center gap-2 text-[10px] text-[#94A3B8]">
                <Clock className="w-3 h-3" />{new Date(r.created_at).toLocaleDateString()}
                <span className="ml-auto">{r.created_by_name}</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {reports.length === 0 && <Card className="col-span-full careiq-card"><CardContent className="py-12 text-center text-[#94A3B8]"><BarChart3 className="w-10 h-10 mx-auto mb-2" /><p>No reports yet</p></CardContent></Card>}
      </div>
    </div>
  );

  // ==================== BUILDER VIEW ====================
  if (mode === 'build') return (
    <div className="space-y-4" data-testid="report-builder">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setMode('list')}><X className="w-4 h-4" /></Button>
          <h1 className="text-xl font-semibold text-[#0A2342]">{editingReportId ? 'Edit Report' : 'Report Builder'}</h1>
        </div>
        <Button className="careiq-btn-primary" onClick={handleGenerate} disabled={generating} data-testid="generate-report-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}{editingReportId ? 'Update' : 'Generate'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: Configuration */}
        <div className="space-y-4">
          <Card className="careiq-card">
            <CardContent className="p-4 space-y-3">
              <div><Label>Report Name *</Label><Input value={reportName} onChange={e => setReportName(e.target.value)} placeholder="e.g. Monthly Patient Summary" data-testid="report-name-input" /></div>
              <div><Label>Description</Label><Input value={reportDesc} onChange={e => setReportDesc(e.target.value)} placeholder="Optional" /></div>
              <div>
                <Label>Data Source</Label>
                <Select value={dataSource} onValueChange={v => { setDataSource(v); setSelectedColumns([]); setSelectedFormId(''); }}>
                  <SelectTrigger data-testid="data-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DATA_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {dataSource === 'form_submissions' && (
                <div><Label>Select Form</Label>
                  <Select value={selectedFormId} onValueChange={v => { setSelectedFormId(v); setSelectedColumns([]); }}>
                    <SelectTrigger data-testid="form-select"><SelectValue placeholder="Choose form" /></SelectTrigger>
                    <SelectContent>{forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="border-t pt-3">
                <Label className="flex items-center gap-1"><Share2 className="w-3 h-3" />Join With</Label>
                <Select value={joinSource || 'none'} onValueChange={v => { const nv = v === 'none' ? '' : v; setJoinSource(nv); setJoinFormId(''); }}>
                  <SelectTrigger><SelectValue placeholder="None (single source)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {dataSource !== 'patients' && <SelectItem value="patients">Patient Records</SelectItem>}
                    {dataSource === 'patients' && <SelectItem value="form_submissions">Form Data</SelectItem>}
                  </SelectContent>
                </Select>
                {joinSource === 'form_submissions' && (
                  <Select value={joinFormId} onValueChange={setJoinFormId}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="Select form to join" /></SelectTrigger>
                    <SelectContent>{forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <div className="border-t pt-3 grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Date From</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Date To</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs" /></div>
              </div>
            </CardContent>
          </Card>

          <Card className="careiq-card">
            <CardContent className="p-4 space-y-3">
              <Label>Visualization</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {CHART_TYPES.map(ct => (
                  <button key={ct.value} onClick={() => setChartType(ct.value)} className={`p-2 rounded-lg border text-center text-[10px] font-medium transition-all ${chartType === ct.value ? 'border-[#0E7490] bg-[#0E7490]/5 text-[#0E7490]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'}`} data-testid={`chart-type-${ct.value}`}>
                    {ct.label}
                  </button>
                ))}
              </div>
              {availableFields.length > 0 && (
                <>
                  <div><Label className="text-xs">Group By</Label>
                    <Select value={groupByField || 'none'} onValueChange={(v) => setGroupByField(v === 'none' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">None</SelectItem>{availableFields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Sort By</Label>
                    <div className="flex gap-1">
                      <Select value={sortField || 'none'} onValueChange={(v) => setSortField(v === 'none' ? '' : v)}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">None</SelectItem>{availableFields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}><ArrowUpDown className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Column Picker */}
        <div className="lg:col-span-2">
          <Card className="careiq-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Select Columns ({selectedColumns.length}/{availableFields.length})</CardTitle>
                <button className="text-[10px] text-[#0E7490] hover:underline" onClick={() => setSelectedColumns(selectedColumns.length === availableFields.length ? [] : [...availableFields])}>
                  {selectedColumns.length === availableFields.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-[300px] overflow-y-auto">
                {availableFields.map(f => (
                  <label key={f.key} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-xs transition-colors ${selectedColumns.some(c => c.key === f.key) ? 'bg-[#0E7490]/10 border border-[#0E7490]/30' : 'bg-[#F8FAFC] border border-transparent hover:border-[#E2E8F0]'}`}>
                    <input type="checkbox" checked={selectedColumns.some(c => c.key === f.key)} onChange={() => toggleColumn(f)} className="rounded" />
                    <span className={`truncate ${f.joined ? 'text-[#7C3AED]' : 'text-[#0A2342]'}`}>{f.label}</span>
                    <span className="text-[8px] text-[#94A3B8] ml-auto shrink-0">{f.type}</span>
                  </label>
                ))}
              </div>
              {availableFields.length === 0 && <p className="text-center py-6 text-xs text-[#94A3B8]">{dataSource === 'form_submissions' ? 'Select a form to see available fields' : 'No fields available'}</p>}

              {/* Selected columns order */}
              {selectedColumns.length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <Label className="text-xs text-[#64748B] mb-1 block">Column Order (drag to reorder)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedColumns.map((c, i) => (
                      <div key={c.key} className="flex items-center gap-1 px-2 py-1 bg-[#0E7490]/10 text-[#0E7490] rounded text-[10px] font-medium">
                        <GripVertical className="w-2.5 h-2.5 text-[#94A3B8]" />
                        {c.label}
                        <button onClick={() => setSelectedColumns(prev => prev.filter(cc => cc.key !== c.key))}><X className="w-2.5 h-2.5 text-[#991B1B]" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  // ==================== VIEW ====================
  return (
    <div className="space-y-4" data-testid="report-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => { setMode('list'); setViewReport(null); setReportData(null); }}><X className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl font-semibold text-[#0A2342]">{viewReport?.name}</h1>
            {viewReport?.description && <p className="text-xs text-[#64748B]">{viewReport.description}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-[#F1F5F9] rounded-lg p-0.5">
            {CHART_TYPES.map(ct => (
              <button key={ct.value} onClick={() => setChartType(ct.value)} className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${chartType === ct.value ? 'bg-white text-[#0E7490] shadow-sm' : 'text-[#64748B] hover:text-[#0A2342]'}`}>{ct.label}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv}><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => openBuilder(viewReport)} data-testid="edit-current-report"><Edit className="w-3.5 h-3.5 mr-1" />Edit</Button>
          <Button variant="outline" size="sm" onClick={() => handleDelete(viewReport.id)} className="text-[#991B1B]"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
        </div>
      </div>

      {/* Charts */}
      {chartType !== 'table' && renderChart()}

      {/* Scorecards — always show summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ScorecardWidget label="Total Records" value={reportData?.total || 0} color="#0E7490" />
        {reportData?.summary && Object.entries(reportData.summary).map(([k, v]) => (
          <ScorecardWidget key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? Object.entries(v).map(([kk,vv]) => `${kk}: ${vv}`).join(', ') : v} color="#14B8A6" />
        ))}
      </div>

      {/* Data Table */}
      <Card className="careiq-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-[#64748B]">{filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''}</span>
            <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-[#94A3B8]" /><Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." className="pl-8 h-8 w-48 text-xs" data-testid="report-search" /></div>
          </div>
          <div className="overflow-x-auto border rounded-lg max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0"><tr className="bg-[#F1F5F9]">
                {(reportData?.columns || []).map(c => <th key={c.key} className="px-3 py-2 text-left text-xs font-medium text-[#64748B] border-b whitespace-nowrap cursor-pointer hover:text-[#0A2342]" onClick={() => { setSortField(c.key); setSortDir(prev => prev === 'asc' ? 'desc' : 'asc'); }}>{c.label} <ArrowUpDown className="w-2.5 h-2.5 inline" /></th>)}
              </tr></thead>
              <tbody>
                {filteredRows.sort((a, b) => {
                  if (!sortField) return 0;
                  const va = a[sortField] || '', vb = b[sortField] || '';
                  return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
                }).map((row, i) => (
                  <tr key={row.id || i} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    {(reportData?.columns || []).map(c => <td key={c.key} className="px-3 py-2 text-xs text-[#0A2342] max-w-[200px] truncate">{c.key === 'submitted_at' || c.key === 'created_at' || c.key === 'recorded_at' ? (row[c.key] ? new Date(row[c.key]).toLocaleString() : '') : (row[c.key] || '—')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
